import {AxiosRequestConfig, AxiosResponse} from 'axios';
import moment from 'moment';
import {StringValue} from 'ms';
import {ActivitiesGroup} from '../backend/interfaces/activities-group';
import {ActivitiesSector} from '../backend/interfaces/activities-sector';
import {ActivityType} from '../backend/interfaces/activity-type';
import {CarePlanConfiguration} from '../backend/interfaces/care-plan-configuration';
import {CarePlanConfigurationItemAutocompleteItem} from '../backend/interfaces/care-plan-configuration-item-autocomplete-item';
import {MainDatabase, MainDatabaseStore, MainDatabaseSyncLog} from '../databases/main';
import {BackendApiManager} from '../managers/backend-api';
import {MAX_LIMIT_ENTRIES} from '../utils/constants';
import {Logger} from '../utils/managers/logger';
import {PaginatedArrayResponse} from './interfaces/paginated-array-response';

export class EntityActivitiesRepository {
  // Singleton Instance
  private static _instance: EntityActivitiesRepository;

  // Private
  private logger: Logger;

  static get instance(): EntityActivitiesRepository {
    if (!this._instance) {
      this._instance = new EntityActivitiesRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('EntityActivitiesRepository');
  }

  private DEFAULT_EXPIRATION: StringValue = '30d';
  private ACTIVITY_TYPES_CACHE_LAST_UPDATE: Date = moment('2025-08-06').toDate();

  public async getSectors(
    skip: number,
    limit: number,
    entityId: string
  ): Promise<PaginatedArrayResponse<ActivitiesSector>> {
    // Set url
    const url: string = `/entities/${entityId}/activities/sectors?skip=${skip}&limit=${limit}&localize=true`;

    // Get Activities Sectors from backend
    const response: AxiosResponse<Array<ActivitiesSector>> =
      await BackendApiManager.instance.get<Array<ActivitiesSector>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  public async getAllSectors(entityId: string): Promise<Array<ActivitiesSector>> {
    const syncLogId = `Entity:${entityId}|ActivitiesSectors`;
    const syncLog = await MainDatabase.instance.getById<MainDatabaseSyncLog>(
      MainDatabaseStore.SyncLogs,
      syncLogId
    );

    if (!syncLog || MainDatabase.instance.isDataExpired(syncLog.date)) {
      const url: string = `/entities/${entityId}/activities/sectors?skip=${0}&limit=${MAX_LIMIT_ENTRIES}&localize=true`;
      const response: AxiosResponse<Array<ActivitiesSector>> =
        await BackendApiManager.instance.get<Array<ActivitiesSector>>(url);

      const putPromises: Array<Promise<void>> = [];
      for (const sector of response.data) {
        putPromises.push(MainDatabase.instance.put(MainDatabaseStore.ActivitiesSectors, sector));
      }

      putPromises.push(
        MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
          id: syncLogId,
          date: new Date(),
        })
      );
      await Promise.all(putPromises);
    }

    const results: Array<ActivitiesSector> = [];
    let cursor = await MainDatabase.instance.openCursor(MainDatabaseStore.ActivitiesSectors);
    while (cursor) {
      if (!cursor.value.entityId || cursor.value.entityId === entityId) {
        results.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return results;
  }

  public async getGroups(
    skip: number,
    limit: number,
    entityId: string,
    activitiesSectorId: string
  ): Promise<PaginatedArrayResponse<ActivitiesGroup>> {
    // Set url
    const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups?skip=${skip}&limit=${limit}&localize=true`;

    // Get Activities Groups from backend
    const response: AxiosResponse<Array<ActivitiesGroup>> =
      await BackendApiManager.instance.get<Array<ActivitiesGroup>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  public async getAllGroups(
    entityId: string,
    activitiesSectorId: string
  ): Promise<Array<ActivitiesGroup>> {
    const syncLogId = `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups`;
    const syncLog = await MainDatabase.instance.getById<MainDatabaseSyncLog>(
      MainDatabaseStore.SyncLogs,
      syncLogId
    );

    if (!syncLog || MainDatabase.instance.isDataExpired(syncLog.date)) {
      const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups?skip=0&limit=${MAX_LIMIT_ENTRIES}&localize=true`;
      const response: AxiosResponse<Array<ActivitiesGroup>> =
        await BackendApiManager.instance.get<Array<ActivitiesGroup>>(url);

      const now = new Date();
      const putPromises: Array<Promise<void>> = [];
      for (const group of response.data) {
        putPromises.push(MainDatabase.instance.put(MainDatabaseStore.ActivitiesGroups, group));
        putPromises.push(
          MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
            id: `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${group.id}`,
            date: now,
          })
        );
      }

      putPromises.push(
        MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
          id: syncLogId,
          date: now,
        })
      );
      await Promise.all(putPromises);
    }

    const results: Array<ActivitiesGroup> = [];
    let cursor = await MainDatabase.instance.openCursor(MainDatabaseStore.ActivitiesGroups);
    while (cursor) {
      if (
        cursor.value.activitiesSectorId === activitiesSectorId &&
        (!cursor.value.entityId || cursor.value.entityId === entityId)
      ) {
        results.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return results;
  }

  public async getGroup(
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string
  ): Promise<ActivitiesGroup | null> {
    const syncLogId = `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}`;
    const syncLog = await MainDatabase.instance.getById<MainDatabaseSyncLog>(
      MainDatabaseStore.SyncLogs,
      syncLogId
    );

    if (!syncLog || MainDatabase.instance.isDataExpired(syncLog.date)) {
      const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}?localize=true`;
      const response: AxiosResponse<ActivitiesGroup> =
        await BackendApiManager.instance.get<ActivitiesGroup>(url);

      const putPromises: Array<Promise<void>> = [];
      putPromises.push(
        MainDatabase.instance.put(MainDatabaseStore.ActivitiesGroups, response.data)
      );
      putPromises.push(
        MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
          id: syncLogId,
          date: new Date(),
        })
      );
      await Promise.all(putPromises);

      return response.data;
    }

    const activitiesGroup = await MainDatabase.instance.getById<ActivitiesGroup>(
      MainDatabaseStore.ActivitiesGroups,
      activitiesGroupId
    );
    return activitiesGroup || null;
  }

  public async getGroupCarePlanConfigurations(
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string
  ): Promise<Array<CarePlanConfiguration>> {
    const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/care-plan-configurations?localize=true`;
    const response: AxiosResponse<Array<CarePlanConfiguration>> =
      await BackendApiManager.instance.get<Array<CarePlanConfiguration>>(url);

    return response.data;

    // ----------- Caching CarePlanConfiguration data doesn't work properly. Disabling it for now. -----------
    // const syncLogId = `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}|CarePlanConfigurations`;
    // const syncLog = await MainDatabase.instance.getById<MainDatabaseSyncLog>(
    //   MainDatabaseStore.SyncLogs,
    //   syncLogId
    // );

    // if (!syncLog || MainDatabase.instance.isDataExpired(syncLog.date)) {
    //   const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/care-plan-configurations?localize=true`;
    //   const response: AxiosResponse<Array<CarePlanConfiguration>> =
    //     await BackendApiManager.instance.get<Array<CarePlanConfiguration>>(url);

    //   const now = new Date();
    //   const putPromises: Array<Promise<void>> = [];
    //   for (const configuration of response.data) {
    //     putPromises.push(
    //       MainDatabase.instance.put(MainDatabaseStore.CarePlanConfigurations, configuration)
    //     );
    //     putPromises.push(
    //       MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
    //         id: `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}|CarePlanConfigurations:${configuration.id}`,
    //         date: now,
    //       })
    //     );
    //   }

    //   putPromises.push(
    //     MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
    //       id: syncLogId,
    //       date: now,
    //     })
    //   );
    //   await Promise.all(putPromises);
    // }

    // const results: Array<CarePlanConfiguration> = [];
    // let cursor = await MainDatabase.instance.openCursor(MainDatabaseStore.CarePlanConfigurations);
    // while (cursor) {
    //   if (!cursor.value.entityId || cursor.value.entityId === entityId) {
    //     results.push(cursor.value);
    //   }
    //   cursor = await cursor.continue();
    // }
    // return results;
  }

  public async getTypes(
    skip: number,
    limit: number,
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string,
    config?: AxiosRequestConfig
  ): Promise<PaginatedArrayResponse<ActivityType>> {
    // Set url
    const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/types?skip=${skip}&limit=${limit}&localize=true`;

    // Get Activities Groups from backend
    const response: AxiosResponse<Array<ActivityType>> = await BackendApiManager.instance.get<
      Array<ActivityType>
    >(url, config);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  public async getAllTypes(
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string
  ): Promise<Array<ActivityType>> {
    const syncLogId = `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}|ActivityTypes`;
    const syncLog = await MainDatabase.instance.getById<MainDatabaseSyncLog>(
      MainDatabaseStore.SyncLogs,
      syncLogId
    );

    if (
      !syncLog ||
      MainDatabase.instance.isDataExpired(
        syncLog.date,
        this.DEFAULT_EXPIRATION,
        this.ACTIVITY_TYPES_CACHE_LAST_UPDATE
      )
    ) {
      const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/types?skip=${0}&limit=${MAX_LIMIT_ENTRIES}&localize=true`;
      const response: AxiosResponse<Array<ActivityType>> =
        await BackendApiManager.instance.get<Array<ActivityType>>(url);

      const now = new Date();
      const putPromises: Array<Promise<void>> = [];
      for (const type of response.data) {
        putPromises.push(MainDatabase.instance.put(MainDatabaseStore.ActivityTypes, type));
        putPromises.push(
          MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
            id: `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}|ActivityTypes:${type.id}`,
            date: now,
          })
        );
      }

      putPromises.push(
        MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
          id: syncLogId,
          date: now,
        })
      );
      await Promise.all(putPromises);
    }

    const results: Array<ActivityType> = [];
    let cursor = await MainDatabase.instance.openCursor(MainDatabaseStore.ActivityTypes);
    while (cursor) {
      if (
        cursor.value.activitiesGroupId === activitiesGroupId &&
        (!cursor.value.entityId || cursor.value.entityId === entityId)
      ) {
        results.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return results;
  }

  public async getType(
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string,
    activityTypeId: string
  ): Promise<ActivityType | null> {
    const syncLogId = `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}|ActivityTypes:${activityTypeId}`;
    const syncLog = await MainDatabase.instance.getById<MainDatabaseSyncLog>(
      MainDatabaseStore.SyncLogs,
      syncLogId
    );

    if (
      !syncLog ||
      MainDatabase.instance.isDataExpired(
        syncLog.date,
        this.DEFAULT_EXPIRATION,
        this.ACTIVITY_TYPES_CACHE_LAST_UPDATE
      )
    ) {
      const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/types/${activityTypeId}?localize=true`;
      const response: AxiosResponse<ActivityType> =
        await BackendApiManager.instance.get<ActivityType>(url);

      const putPromises: Array<Promise<void>> = [];
      putPromises.push(MainDatabase.instance.put(MainDatabaseStore.ActivityTypes, response.data));
      putPromises.push(
        MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
          id: syncLogId,
          date: new Date(),
        })
      );
      await Promise.all(putPromises);

      return response.data;
    }

    const activityType = await MainDatabase.instance.getById<ActivityType>(
      MainDatabaseStore.ActivityTypes,
      activityTypeId
    );
    return activityType || null;
  }

  public async getTypeCarePlanConfigurations(
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string,
    activityTypeId: string
  ): Promise<Array<CarePlanConfiguration>> {
    const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/types/${activityTypeId}/care-plan-configurations?localize=true`;
    const response: AxiosResponse<Array<CarePlanConfiguration>> =
      await BackendApiManager.instance.get<Array<CarePlanConfiguration>>(url);

    return response.data;

    // ----------- Caching CarePlanConfiguration data doesn't work properly. Disabling it for now. -----------
    // const syncLogId = `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}|ActivityTypes:${activityTypeId}|CarePlanConfigurations`;
    // const syncLog = await MainDatabase.instance.getById<MainDatabaseSyncLog>(
    //   MainDatabaseStore.SyncLogs,
    //   syncLogId
    // );

    // if (!syncLog || MainDatabase.instance.isDataExpired(syncLog.date)) {
    //   const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/types/${activityTypeId}/care-plan-configurations?localize=true`;
    //   const response: AxiosResponse<Array<CarePlanConfiguration>> =
    //     await BackendApiManager.instance.get<Array<CarePlanConfiguration>>(url);

    //   const now = new Date();
    //   const putPromises: Array<Promise<void>> = [];
    //   for (const configuration of response.data) {
    //     putPromises.push(
    //       MainDatabase.instance.put(MainDatabaseStore.CarePlanConfigurations, configuration)
    //     );
    //     putPromises.push(
    //       MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
    //         id: `Entity:${entityId}|ActivitiesSectors:${activitiesSectorId}|ActivitiesGroups:${activitiesGroupId}|ActivityTypes:${activityTypeId}|CarePlanConfigurations:${configuration.id}`,
    //         date: now,
    //       })
    //     );
    //   }

    //   putPromises.push(
    //     MainDatabase.instance.put(MainDatabaseStore.SyncLogs, {
    //       id: syncLogId,
    //       date: now,
    //     })
    //   );
    //   await Promise.all(putPromises);
    // }

    // const results: Array<CarePlanConfiguration> = [];
    // let cursor = await MainDatabase.instance.openCursor(MainDatabaseStore.CarePlanConfigurations);
    // while (cursor) {
    //   if (!cursor.value.entityId || cursor.value.entityId === entityId) {
    //     results.push(cursor.value);
    //   }
    //   cursor = await cursor.continue();
    // }
    // return results;
  }

  public async getCareplanConfigurationOptionItems(
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string,
    activityTypeId: string,
    carePlanConfigurationId: string,
    skip: number,
    limit: number,
    userId?: string,
    name?: string
  ): Promise<{totalCount: number; data: Array<CarePlanConfigurationItemAutocompleteItem>}> {
    // Set url
    let url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/types/${activityTypeId}/care-plan-configurations/${carePlanConfigurationId}/items?skip=${skip}&limit=${limit}&localize=true`;
    if (userId) url += `&userId=${userId}`;
    if (name) url += `&name=${name}`;

    // Get's current item autocomplete options
    const response: AxiosResponse<Array<CarePlanConfigurationItemAutocompleteItem>> =
      await BackendApiManager.instance.get<Array<CarePlanConfigurationItemAutocompleteItem>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  public async getGroupCarePlanConfigurationDataValue(
    entityId: string,
    activitiesSectorId: string,
    activitiesGroupId: string,
    activityTypeId: string,
    carePlanConfigurationId: string,
    carePlanConfigurationItemId: string
  ): Promise<CarePlanConfigurationItemAutocompleteItem> {
    // Set url
    const url: string = `/entities/${entityId}/activities/sectors/${activitiesSectorId}/groups/${activitiesGroupId}/types/${activityTypeId}/care-plan-configurations/${carePlanConfigurationId}/items/${carePlanConfigurationItemId}?localize=true`;

    // Get's the current field value
    const response: AxiosResponse<CarePlanConfigurationItemAutocompleteItem> =
      await BackendApiManager.instance.get<CarePlanConfigurationItemAutocompleteItem>(url);

    // Return
    return response.data;
  }
}
